import { motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import { sectionAnimVariants } from '../../constants/animationVariants';
import { APPOINTMENT_CARDS_PER_PAGE } from '../../constants/settings';
import useRetainAgreementActions from '../../hooks/APIHooks/appointments/useRetainAgreementActions';
import useCheckUserFeaturePermissions from '../../hooks/useCheckUserFeaturePermissions';
import AcademiaNotFound from '../CommonComps/AcademiaNotFound';
import RetainAgreementCard from '../CommonComps/Cards/RetainAgreementCard';
import AcademiaLoading from '../CommonComps/Loadings/AcademiaLoading';
import NoTableDataFound from '../CommonComps/NoTableDataFound';
import DashboardTablePagination from '../CommonComps/Paginations/DashboardTablePagination';
import PageHeader from '../CommonComps/Titles/PageHeader';
import CreateAgreementAction from './CreateAgreementAction';

const ShowAllRetainAgreements = () => {
  // integration of react hooks here
  const [currentPage, setCurrentPage] = useState<number>(1);

  // integration of custom API hooks here
  const { getRetainAgreementListHandler } = useRetainAgreementActions(
    'retainAgreementList'
  );

  // integration of custom hooks here
  const { featureBasedPermissions } =
    useCheckUserFeaturePermissions('retainerAgreement');

  const retainAgreementList = useMemo(() => {
    if (!getRetainAgreementListHandler.data?.data) return [];

    return getRetainAgreementListHandler.data.data;
  }, [getRetainAgreementListHandler.data]);

  // calculating pages for pagination here
  const totalPages = Math.ceil(
    retainAgreementList.length / APPOINTMENT_CARDS_PER_PAGE
  );
  const startIndex = (currentPage - 1) * APPOINTMENT_CARDS_PER_PAGE;
  const endIndex = startIndex + APPOINTMENT_CARDS_PER_PAGE;

  // rendering show all retain agreements section component here
  return (
    <section>
      {getRetainAgreementListHandler.isLoading ? (
        <AcademiaLoading />
      ) : getRetainAgreementListHandler.isSuccess ? (
        featureBasedPermissions?.includes('READ') ? (
          <>
            <PageHeader
              headerText='All Retain Agreements'
              additionalComponent={
                <CreateAgreementAction
                  hasCreatePermission={
                    featureBasedPermissions.includes('CREATE') || false
                  }
                />
              }
            />

            {retainAgreementList.length ? (
              <motion.div
                variants={sectionAnimVariants}
                initial='initial'
                animate='animate'
                className='space-y-5'
              >
                <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5 px-2'>
                  {retainAgreementList
                    .slice(startIndex, endIndex)
                    .map((agreement) => (
                      <RetainAgreementCard
                        key={agreement.id}
                        retainAgreement={agreement}
                        hasDeletePermission={featureBasedPermissions.includes(
                          'DELETE'
                        )}
                        hasUpdatePermission={featureBasedPermissions.includes(
                          'UPDATE'
                        )}
                      />
                    ))}
                </div>

                <div className='flex flex-col md:flex-row items-center justify-center md:justify-between gap-3 bg-legal-dark-blue rounded-xl px-5 py-3 shadow-academia-sm'>
                  <p className='text-lg font-medium text-base-white'>
                    Showing{' '}
                    <span className='font-semibold text-legal-brown'>
                      {startIndex + 1}-
                      {endIndex > retainAgreementList.length
                        ? retainAgreementList.length
                        : endIndex}
                    </span>{' '}
                    of{' '}
                    <span className='font-semibold text-legal-brown'>
                      {retainAgreementList.length}
                    </span>
                  </p>

                  <DashboardTablePagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    setCurrentPage={setCurrentPage}
                  />
                </div>
              </motion.div>
            ) : (
              <NoTableDataFound message='No retain agreement data found!!' />
            )}
          </>
        ) : (
          <AcademiaLoading />
        )
      ) : getRetainAgreementListHandler.isError ? (
        <AcademiaNotFound message='Failed to load retain agreement data!!' />
      ) : null}
    </section>
  );
};

export default ShowAllRetainAgreements;
